import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MagicLinkGrantRequest, PasswordGrantTokenRequest } from 'edu-auth/dist/request/access-token.request';
import {
  MagicLinkConsumeRequest,
  MagicLinkRequestPayload,
} from 'edu-auth/dist/request/magic-link.request';
import { AccessTokenErrorResponse, AccessTokenResponse, AccessTokenSuccessResponse } from 'edu-auth/dist/responses/access-token.response';
import { UserInfoResponse } from 'edu-auth/dist/responses/user-info-response';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { OidcAuthService } from '../auth/oidc-auth.service';
import { DevicesService } from './devices.service';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private readonly COOKIE_EXPIRY_DAYS = 365;
  private readonly TOKEN_KEY = "access-token";
  private readonly REFRESH_TOKEN_KEY = "refresh-token";
  private readonly SCOPES_KEY = "scope";

  /**
   * User info for the QLM (OIDC) session, held in memory only -- it lives and dies with
   * the in-memory tokens (nothing session-bearing goes to cookies/localStorage on the
   * OIDC path), unlike the old path's 365-day user-info cookie.
   */
  private _oidcUserInfo?: UserInfoResponse;

  constructor(
    private _cookieService: CookieService,
    private _client: HttpClient,
    private devicesService: DevicesService,
    private _oidcAuth: OidcAuthService
  ) {
    // Mirror a logout that happened in ANOTHER tab: with tokens now in shared localStorage,
    // one tab's logout removes them for all, so clear this tab's in-memory state and reload
    // to /login rather than keep acting logged-in on stale tokens. Deliberately does not
    // re-broadcast (every tab already got the message).
    this._oidcAuth.remoteLogout$.subscribe((target) => this.applyRemoteLogout(target));
  }

  private applyRemoteLogout(targetUrl: string): void {
    this._oidcUserInfo = undefined;
    this._oidcAuth.clearLocalSession();
    // Full page load discards all in-memory service state; follow the originating tab to the
    // same destination -- translator /login for a forced logout (silent re-auth against the
    // still-alive AS session), or the main app for a deliberate "leave the translator".
    window.location.assign(targetUrl);
  }

  /**
   * True because the user-initiated sign-out (`signOutToMainApp`) redirects the browser
   * itself (a full-page navigation to the main app). Callers must NOT do their own
   * client-side navigation, or it races with — and can override — that redirect (e.g. the
   * login page's silent re-auth).
   */
  public get logoutRedirects(): boolean {
    return true;
  }

  public get isLoggedIn(): boolean {
    return this._oidcAuth.isLoggedIn;
  }

  public get hasInfo(): boolean {
    return this._oidcUserInfo != undefined;
  }

  public get access_token(): string {
    return this._oidcAuth.accessToken;
  }

  public get isAdmin(): boolean {
    let info = this.userInfo;
    return info.type != 'student';
  }

  public get refresh_token(): string | undefined {
    // The OAuth library refreshes its own tokens; the old path's refresh-token
    // header must not be sent alongside a QLM bearer.
    return undefined;
  }

  public get scopes(): string[] {
    return this._oidcAuth.grantedScopes;
  }

  public async getUserInfo(): Promise<UserInfoResponse> {
    if (this._oidcUserInfo) {
      return this._oidcUserInfo;
    }
    // Dedicated route: /auth/user-info can't decode QLM's opaque tokens.
    this._oidcUserInfo = await this._client
      .get<UserInfoResponse>(`${environment.backend_url}/translator-questions/user-info`)
      .toPromise();
    return this._oidcUserInfo;
  }

  public get userInfo(): UserInfoResponse {
    if (!this._oidcUserInfo) {
      throw new Error('User is not logged in');
    }
    return this._oidcUserInfo;
  }
  /**
   * Forced/full local cleanup, used by the route guard and the 401 interceptor when the
   * session is invalid — NOT the friendly user-initiated sign-out (see `signOutToMainApp`).
   * Drops this app's in-memory session and does NOT navigate; the caller then routes to
   * /login for (silent) re-auth.
   */
  public logout(): void {
    this._oidcUserInfo = undefined;
    this._oidcAuth.clearLocalSession();
    // Forced/invalid-session logout: siblings recover via the translator's own /login.
    this._oidcAuth.broadcastLogout('/login');
  }

  /**
   * User-initiated "leave the translator" = LOCAL logout. Clears only THIS app's in-memory
   * session and lands the user on their QLM account (the main app, `frontend_url`).
   * Deliberately does NOT hit the AS `end_session` endpoint, so the QLM session stays alive
   * and reopening the translator silently re-signs the user in.
   * Performs the browser redirect itself; callers must not also navigate (it would race).
   */
  public signOutToMainApp(): void {
    this._oidcUserInfo = undefined;
    this._oidcAuth.clearLocalSession();
    // Deliberate "leave the translator": send this tab AND every sibling to the main app --
    // NOT /login, which would silently re-auth them straight back into the translator. Fall
    // back to /login only if no main-app URL is configured (both paths use the same target
    // so the originating tab is never left stranded on a token-less page).
    const target = environment.frontend_url || '/login';
    this._oidcAuth.broadcastLogout(target);
    window.location.assign(target);
  }

  public async login(tokenRequest: PasswordGrantTokenRequest): Promise<AccessTokenResponse> {
    try {
      // Do not wipe cookies before the request: a failed login attempt must
      // not log out an existing session. setTokens overwrites on success.
      let deviceInfo = await this.devicesService.getDeviceInfo();
      tokenRequest = {
        ...tokenRequest,
        device: deviceInfo
      }
      let response = await this._client.post<AccessTokenResponse>(`${environment.backend_url}/auth/token`, tokenRequest).toPromise();
      this.setTokens(response);
      // New session established: drop any stale cached user-info so we fetch
      // the info belonging to the account that just logged in.
      this._oidcUserInfo = undefined;
      return response;
    } catch (e) {
      throw new Error(e.error.message);
    }
  }

  setTokens(response: AccessTokenResponse) {
    let success_response = response as AccessTokenSuccessResponse
    if (success_response.access_token) {
      this._cookieService.set(this.TOKEN_KEY, success_response.access_token, { expires: this.COOKIE_EXPIRY_DAYS, secure: environment.production, path: '/', domain: environment.domain });
      if (success_response.refresh_token != undefined) {
        this._cookieService.set(this.REFRESH_TOKEN_KEY, success_response.refresh_token, { expires: this.COOKIE_EXPIRY_DAYS, secure: environment.production, path: '/', domain: environment.domain });
      }
      this._cookieService.set(this.SCOPES_KEY, success_response.scope, { expires: this.COOKIE_EXPIRY_DAYS, secure: environment.production, path: '/', domain: environment.domain });
    } else {
      throw new Error(`Error occurred ${(response as AccessTokenErrorResponse).error_description}`);
    }
  }

  async checkDeviceId(deviceId: string): Promise<boolean> {
    try {
      let info = await this._client.get<any>(`${environment.backend_url}/auth/device-info/${deviceId}`).toPromise();
      return info.state;
    } catch (e) {
      return false;
    }
  }

  public async refreshToken(_newToken: string) {
    // OIDC-only: the OAuth library owns token refresh and the backend never rotates QLM
    // tokens via response headers, so this is a no-op.
  }

  /**
   * Recover an expired QLM access token via the rotating refresh token. Used by the 401
   * interceptor to retry a failed request before forcing re-auth. Resolves false when the
   * refresh fails (refresh token gone/revoked or AS session ended) -- the caller then logs
   * out and routes to /login.
   */
  public async refreshSession(): Promise<boolean> {
    return this._oidcAuth.refreshToken();
  }

  public renewCookieExpiry(): void {
    // OIDC-only: the session lives in memory, not in cookies, so there is nothing to renew.
  }

  public async requestMagicLink(payload: MagicLinkRequestPayload): Promise<void> {
    try {
      await this._client
        .post(`${environment.backend_url}/auth/magic-link/request`, payload)
        .toPromise();
    } catch (e: unknown) {
      throw new Error(
        this.formatMagicLinkRequestError(
          this.extractHttpErrorMessage(e) ?? 'Unable to send login link.',
        ),
      );
    }
  }

  public async consumeMagicLink(payload: MagicLinkConsumeRequest): Promise<AccessTokenResponse> {
    const trimmedToken = payload.token?.trim();
    const trimmedEmail = payload.email?.trim();
    const trimmedCode = payload.code?.trim();
    if (!trimmedToken && (!trimmedEmail || !trimmedCode)) {
      throw new Error('Missing login credentials');
    }
    try {
      const deviceInfo = await this.devicesService.getDeviceInfo();
      const tokenRequest: MagicLinkGrantRequest = {
        grant_type: 'magic_link',
        scope: '',
        device: deviceInfo,
        ...(trimmedToken
          ? { token: trimmedToken }
          : { email: trimmedEmail, code: trimmedCode }),
      };
      const response = await this._client
        .post<AccessTokenResponse>(
          `${environment.backend_url}/auth/token`,
          tokenRequest,
        )
        .toPromise();
      this.setTokens(response);
      return response;
    } catch (e: unknown) {
      throw new Error(
        this.extractTokenEndpointErrorMessage(e) ??
          'Code is invalid or expired.',
      );
    }
  }

  /** Strips Nest ValidationError prefixes and normalizes magic-link copy for the UI. */
  private formatMagicLinkRequestError(message: string): string {
    const stripped = message.replace(/^Invalid\s+[\w]+\s*:\s*/i, '').trim();
    const cooldownMatch = stripped.match(
      /^please wait (\d+) second\(s\) before requesting another login link\.?$/i,
    );
    if (cooldownMatch) {
      const seconds = Number.parseInt(cooldownMatch[1], 10);
      const unit = seconds === 1 ? 'second' : 'seconds';
      return `Please wait ${seconds} ${unit} before requesting another login link.`;
    }
    return stripped;
  }

  /**
   * Nest validation / magic-link request errors (body.message).
   */
  private extractHttpErrorMessage(error: unknown): string | undefined {
    if (error instanceof HttpErrorResponse) {
      const body = error.error;
      if (typeof body === 'string') {
        return body;
      }
      if (body && typeof body === 'object' && 'message' in body) {
        const m = (body as { message?: string | string[] }).message;
        return Array.isArray(m) ? m[0] : m;
      }
    }
    const err = error as {
      error?: { message?: string | string[] } | string;
    };
    if (typeof err?.error === 'string') {
      return err.error;
    }
    if (err?.error && typeof err.error === 'object' && 'message' in err.error) {
      const m = (err.error as { message?: string | string[] }).message;
      return Array.isArray(m) ? m[0] : m;
    }
    return undefined;
  }

  /**
   * OAuth-style token endpoint errors (error_description or message).
   */
  private extractTokenEndpointErrorMessage(error: unknown): string | undefined {
    const err = error as {
      error?: {
        error_description?: string;
        message?: string | string[];
      } | string;
    };
    if (typeof err?.error === 'string') {
      return err.error;
    }
    if (err?.error && typeof err.error === 'object') {
      const body = err.error as {
        error_description?: string;
        message?: string | string[];
      };
      if (body.error_description) {
        return body.error_description;
      }
      if (body.message) {
        const m = body.message;
        return Array.isArray(m) ? m[0] : m;
      }
    }
    return undefined;
  }
}
